import React, { useState } from 'react';
import styled from 'styled-components';
import { formatComitenteForDisplay, formatNumberForDisplay, formatNumberForInput, parseAmountForState } from '../utils/formatters';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  box-shadow: ${props => props.theme.shadows.deep};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-family: ${props => props.theme.typography.titleFamily};
  color: ${props => props.theme.colors.secondary};
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.textSecondary};
  padding: 0;
  &:hover {
    color: ${props => props.theme.colors.secondary};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.secondary};
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius};
  font-size: 1rem;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.secondary};
  background-color: ${props => props.theme.colors.background};
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius};
  font-size: 1rem;
  font-family: ${props => props.theme.typography.fontFamily};
  color: ${props => props.theme.colors.secondary};
  background-color: ${props => props.theme.colors.background};
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  transition: background-color 0.2s;
`;

const SubmitButton = styled(Button)`
  background-color: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.textPrimary};
  &:hover {
    background-color: #333;
  }
  &:disabled {
    background-color: ${props => props.theme.colors.lightGray};
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${props => props.theme.colors.lightGray};
  color: ${props => props.theme.colors.textPrimary};
  &:hover {
    background-color: #ccc;
  }
`;

const ErrorMessage = styled.p`
  color: ${props => props.theme.colors.errorText};
  font-size: 0.9rem;
  margin: 0.5rem 0 0 0;
`;

const AddA3MovementModal = ({ isOpen, onClose, onSubmit, clientes, especies }) => {
  const [formData, setFormData] = useState({
    operacion: 'Alta',
    nroComitente: '',
    especie: '',
    vn: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (!formData.nroComitente) {
      setError('Debe seleccionar un comitente');
      return;
    }

    if (!formData.especie) {
      setError('Debe seleccionar una especie');
      return;
    }

    const vn = parseFloat(parseAmountForState(formData.vn));
    if (isNaN(vn) || vn <= 0) {
      setError('El VN debe ser un número mayor a 0');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        operacion: formData.operacion,
        nroComitente: formData.nroComitente,
        especie: formData.especie,
        vn: vn
      });
      
      // Reset form
      setFormData({
        operacion: 'Alta',
        nroComitente: '',
        especie: '',
        vn: ''
      });
      setError('');
    } catch (err) {
      setError(err.message || 'Error al guardar el movimiento');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer className="axon-modal-sheet" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Cargar Movimiento A3</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Operación *</Label>
            <Select
              name="operacion"
              value={formData.operacion}
              onChange={handleChange}
              required
            >
              <option value="Alta">Alta</option>
              <option value="Baja">Baja</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Comitente *</Label>
            <Select
              name="nroComitente"
              value={formData.nroComitente}
              onChange={handleChange}
              required
            >
              <option value="">Seleccione un comitente</option>
              {clientes.map(cliente => (
                <option key={cliente.nroComitente} value={cliente.nroComitente}>
                  {formatComitenteForDisplay(cliente.nroComitente)} - {cliente.nombre}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Especie *</Label>
            <Input
              type="text"
              name="especie"
              value={formData.especie}
              onChange={handleChange}
              list="especies-a3-list"
              placeholder="Seleccione una especie"
              autoComplete="off"
              required
            />
            <datalist id="especies-a3-list">
              {especies.map(esp => (
                <option key={esp.id} value={esp.nombre} />
              ))}
            </datalist>
          </FormGroup>

          <FormGroup>
            <Label>VN (Valor Nominal) *</Label>
            <Input
              type="text"
              inputMode="decimal"
              name="vn"
              value={formData.vn}
              onChange={handleChange}
              onFocus={(e) => setFormData(prev => ({ ...prev, vn: formatNumberForInput(e.target.value) }))}
              onBlur={(e) => {
                const parsed = parseAmountForState(e.target.value);
                setFormData(prev => ({ ...prev, vn: parsed ? formatNumberForDisplay(parsed) : '' }));
              }}
              placeholder="Ingrese el valor nominal"
              required
            />
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonContainer className="axon-modal-actions">
            <CancelButton type="button" onClick={onClose}>
              Cancelar
            </CancelButton>
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : 'Guardar'}
            </SubmitButton>
          </ButtonContainer>
        </Form>
      </ModalContainer>
    </Overlay>
  );
};

export default AddA3MovementModal;
